/**
 * Voucher-type-specific ledger filtering rules according to GroupCODE and account types.
 *
 * GroupCode reference (from Group_mast / HEAD_MAST):
 *   16 = Bank OCC a/c
 *   24 = Bank Accounts
 *   25 = Cash-in-hand
 *   14 = Purchase Account
 *   15 = Sales Account
 *   23 = Sundry Creditors (Suppliers, Vehicle Owners, Vendors)
 *   29 = Sundry Debtors (Clients, Customers, Corporate Accounts)
 *   19 = Expenses (Direct)
 *   20 = Expenses (Indirect)
 *   21 = Duties & Taxes (GST, TDS, etc.)
 *
 * Rules:
 *   - Payment (F5):
 *       Dr side: Creditors, Expenses, Taxes (All non-Cash/Bank accounts)
 *       Cr side: Cash & Bank accounts only
 *   - Receipt (F6):
 *       Dr side: Cash & Bank accounts only
 *       Cr side: Debtors, Incomes (All non-Cash/Bank accounts)
 *   - Contra (F4):
 *       Dr side: Cash & Bank accounts only
 *       Cr side: Cash & Bank accounts only
 *   - Journal / Purchase / Debit Note / Credit Note:
 *       Allow all ledgers or unconstrained for custom entries
 */

export const CASH_BANK_GROUP_CODES = [16, 24, 25];

/**
 * Check if a ledger is a Cash or Bank account based on GroupCODE or Group/Ledger name.
 */
export function isCashOrBankLedger(ledger) {
  if (!ledger) return false;
  const gCode = Number(ledger.groupCode ?? ledger.GroupCODE ?? 0);
  if (CASH_BANK_GROUP_CODES.includes(gCode)) return true;

  const gName = String(ledger.group || ledger.GroupName || ledger.GroupNAME || "").toLowerCase().trim();
  const lName = String(ledger.name || ledger.LedgerName || ledger.HEADName || "").toLowerCase().trim();

  if (
    gName === "cash-in-hand" ||
    gName.includes("bank accounts") ||
    gName.includes("bank occ") ||
    gName.includes("bank od") ||
    gName.startsWith("cash") ||
    gName.startsWith("bank") ||
    gName.endsWith("bank") ||
    gName.endsWith("cash")
  ) {
    return true;
  }

  if (
    lName === "cash" ||
    lName === "cash a/c" ||
    lName === "cash in hand" ||
    lName === "petty cash"
  ) {
    return true;
  }

  return false;
}

export const LEDGER_FILTER_RULES = {
  payment: {
    dr: { excludeCashBank: true }, // Payees: Sundry Creditors, Expenses, Taxes, etc.
    cr: { isCashBankOnly: true },  // Source: Cash & Bank only
  },
  receipt: {
    dr: { isCashBankOnly: true },  // Destination: Cash & Bank only
    cr: { excludeCashBank: true }, // Source: Sundry Debtors, Income, etc.
  },
  contra: {
    dr: { isCashBankOnly: true },  // Cash/Bank to Cash/Bank transfer only
    cr: { isCashBankOnly: true },
  },
  journal: null,      // Allow all ledgers for general journal adjustments
  purchase: null,     // Allow all ledgers (Purchase A/c, GST, Creditors, Freight)
  debit_note: null,   // Allow all ledgers
  credit_note: null,  // Allow all ledgers
};

/**
 * Filter ledgers based on voucher type and row Dr/Cr type.
 *
 * @param {Array} allLedgers - Full ledger list (each must have groupCode or GroupCODE)
 * @param {string} voucherType - Voucher type id (e.g. "payment", "receipt", "journal", "contra")
 * @param {string} rowType - "Dr" or "Cr"
 * @returns {Array} Filtered ledgers
 */
export function getFilteredLedgers(allLedgers, voucherType, rowType) {
  if (!allLedgers || !voucherType) return allLedgers || [];

  // Normalize voucher type key (handle "payment", "Payment (F5)", "debit_note", "Debit Note")
  const normalizedType = String(voucherType)
    .toLowerCase()
    .trim()
    .replace(/\s*\([^)]*\)/g, "") // remove (f5), (f6), etc.
    .replace(/[\s-]+/g, "_");

  const rule = LEDGER_FILTER_RULES[normalizedType];
  if (!rule) return allLedgers;

  const sideRule = rowType === "Cr" ? rule.cr : rule.dr;
  if (!sideRule) return allLedgers;

  return allLedgers.filter((ledger) => {
    const isCashBank = isCashOrBankLedger(ledger);
    const gCode = Number(ledger.groupCode ?? ledger.GroupCODE ?? 0);

    if (sideRule.isCashBankOnly) {
      return isCashBank;
    }
    if (sideRule.excludeCashBank) {
      return !isCashBank;
    }
    if (sideRule.only) {
      return sideRule.only.includes(gCode);
    }
    if (sideRule.exclude) {
      return !sideRule.exclude.includes(gCode);
    }
    return true;
  });
}
